import { useState, useCallback, useEffect, useRef } from "react";
import { Alert, Box, AlertIcon, Text, VStack } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormikContext } from "formik";
import { getBankRailOptionsV2 } from "api/payments/index";
import { useSessionCtx, useSessionJWT, useAnalytics } from "utils";
import { FullPageSpinner } from "components/Spinner";
import BankRailOption from "./BankRailOption";
import { logError } from "api/accounts-customer";

const BankRailOptions = ({ bankAccountId, onPayoutRailsSelection }) => {
  const analytics = useAnalytics();
  const viewEventFired = useRef(false);
  const { formatMessage } = useIntl();
  const { values, setValues } = useFormikContext();
  const { sessionJWT } = useSessionJWT();
  const { sessionId } = useSessionCtx();

  const [isLoading, setIsLoading] = useState(false);
  const [loadingError, setLoadingError] = useState(null);
  const [bankRailOptions, setBankRailOptions] = useState(null);

  useEffect(() => {
    // useEffect for tracking page view analytic
    if (!viewEventFired.current) {
      analytics.page("PayoutV2", "BankRailOptions_Viewed", {
        amount: values.amount,
        country: values.payoutDestination?.country_iso || null,
      });
      viewEventFired.current = true;
    }
  }, [analytics, viewEventFired, values]);

  const loadBankRailOptions = useCallback(
    async (amount) => {
      setIsLoading(true);
      setLoadingError(null);
      setBankRailOptions(null);
      const ctx = {
        ...sessionJWT,
        sessionId,
      };
      try {
        const results = await getBankRailOptionsV2(ctx, {
          bankAccountId,
          currency: values.currency || "USD",
          requested_amount: amount,
          bankRailsVendor: values.bankRailsVendor,
        });
        setBankRailOptions(results.payload);
        // gather options count for analytic
        const options = results.payload?.options;
        const optionsCount = Array.isArray(options) ? options.length : 0;
        analytics.track("PayoutV2_BankRailOptions_Loaded", {
          isSuccessful: true,
          amount: values.amount,
          country: values.payoutDestination?.country_iso || null,
          optionsCount,
        });
      } catch (err) {
        logError(sessionJWT, { message: err.message, flowLoc: "payoutv2.bankRailOptions", data: err.data });
        setLoadingError(err);
        analytics.track("PayoutV2_BankRailOptions_Loaded", {
          isSuccessful: false,
          amount: values.amount,
          country: values.payoutDestination?.country_iso || null,
          optionsCount: null,
        });
      }
      setIsLoading(false);
    },
    [bankAccountId, sessionJWT, sessionId, values, analytics],
  );

  useEffect(() => {
    loadBankRailOptions(values.amount);
  }, [values.amount, loadBankRailOptions]);

  const handlePayoutMethodSelection = (payoutRails) => {
    if (!payoutRails || !bankRailOptions.payment_method_id) return;
    const { payout_requested, payout_fee, payout_total } = payoutRails;
    setValues((prevValues) => {
      return {
        ...prevValues,
        payoutRails,
        destinationPaymentMethodId: bankRailOptions.payment_method_id,
        payoutAmountDetails: { payout_requested, payout_fee, payout_total } // track what we're showing user for amounts with bank options for analytic event
      };
    });
    onPayoutRailsSelection && onPayoutRailsSelection();
  };

  let content = null;
  if (isLoading) {
    content = <FullPageSpinner data-testid="payout-load-bank-rail-options-spinner" />;
  } else if (loadingError) {
    let msg =
      loadingError.message ||
      formatMessage({
        defaultMessage: "There was an error retrieving payout options",
        description: "error message",
      });
    if (Array.isArray(loadingError.data?.codes) && loadingError.data?.codes.includes("INVALID_IBAN")) {
      msg = formatMessage({
        defaultMessage: "Invalid bank details.  Please check the information and add the bank account again.",
        description: "error message",
      });
    }
    content = (
      <Alert status="error">
        <AlertIcon />
        {msg}
      </Alert>
    );
  } else if (bankRailOptions) {
    if (!bankRailOptions.options || bankRailOptions.options.length === 0) {
      content = (
        <Alert status="info">
          <AlertIcon />
          <FormattedMessage
            defaultMessage="There are no payout options for this bank account currently.  Please select another."
            description="error message"
          />
        </Alert>
      );
    } else {
      const { invalid_total, invalid_reason, options } = bankRailOptions;
      content = (
        <VStack>
          {options.map((opt) => (
            <BankRailOption
              key={opt.id}
              option={opt}
              onPayoutMethodSelection={handlePayoutMethodSelection}
              invalidTotal={invalid_total}
              invalidReasonCode={invalid_reason}
              payoutFromCurrency={values.currency}
              payoutFromAmount={values.amount}
            />
          ))}
        </VStack>
      );
    }
  }
  return (
    <Box py="6">
      <Box pb="6">
        <Text fontSize="lg" as="b">
          <FormattedMessage
            defaultMessage="Please select a payout method."
            description="Section header"
          />
        </Text>
      </Box>
      {content}
    </Box>
  );
};

export default BankRailOptions;
